import { z } from 'zod';

export const SubscriptionTraceStepKeySchema = z.enum([
  'PENDING_SIGNUP_CREATED',
  'CHECKOUT_SESSION_CREATED',
  'CHECKOUT_COMPLETED',
  'USER_CREATED',
  'GROUPE_SUBSCRIPTION_ACTIVATED',
  'WELCOME_EMAIL_SENT',
  'VERIFICATION_EMAIL_SENT',
  'REGISTRATION_SMS_SENT',
  'PAYMENT_CONFIRMATION_EMAIL_SENT',
]);

export const SubscriptionTraceStatusSchema = z.enum([
  'SUCCESS',
  'FAILED',
  'SKIPPED',
  'PENDING',
]);

export const SubscriptionTraceProviderSchema = z.enum([
  'APP',
  'STRIPE',
  'EMAIL',
  'SMS',
]);

export const SubscriptionTraceEventSchema = z.object({
  id: z.string(),
  email: z.string(),
  userId: z.number().nullable().optional(),
  groupeId: z.number().nullable().optional(),
  sessionId: z.string().nullable().optional(),
  subscriptionId: z.string().nullable().optional(),
  stepKey: SubscriptionTraceStepKeySchema,
  status: SubscriptionTraceStatusSchema,
  provider: SubscriptionTraceProviderSchema,
  errorMessage: z.string().nullable().optional(),
  payload: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.string(),
});

export const SubscriptionHelperChecklistItemSchema = z.object({
  stepKey: SubscriptionTraceStepKeySchema,
  label: z.string(),
  status: SubscriptionTraceStatusSchema,
  provider: SubscriptionTraceProviderSchema.optional(),
  occurredAt: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  source: z.enum(['TRACE', 'AUDIT', 'DERIVED']),
});

export const SubscriptionHelperSubjectSchema = z.object({
  email: z.string().nullable().optional(),
  userId: z.number().nullable().optional(),
  groupeId: z.number().nullable().optional(),
  sessionId: z.string().nullable().optional(),
  subscriptionId: z.string().nullable().optional(),
  subscriptionStatus: z.string().nullable().optional(),
  planType: z.string().nullable().optional(),
  tierId: z.string().nullable().optional(),
});

export const SubscriptionHelperResponseSchema = z.object({
  subject: SubscriptionHelperSubjectSchema,
  checklist: z.array(SubscriptionHelperChecklistItemSchema),
  rawEvents: z.array(SubscriptionTraceEventSchema),
  warnings: z.array(z.string()),
});

export const SubscriptionHelperQuerySchema = z.object({
  email: z.string().email().optional(),
  userId: z.coerce.number().int().positive().optional(),
  groupeId: z.coerce.number().int().positive().optional(),
  sessionId: z.string().min(1).optional(),
});

export type SubscriptionTraceStepKey = z.infer<typeof SubscriptionTraceStepKeySchema>;
export type SubscriptionTraceStatus = z.infer<typeof SubscriptionTraceStatusSchema>;
export type SubscriptionTraceProvider = z.infer<typeof SubscriptionTraceProviderSchema>;
export type SubscriptionTraceEvent = z.infer<typeof SubscriptionTraceEventSchema>;
export type SubscriptionHelperChecklistItem = z.infer<typeof SubscriptionHelperChecklistItemSchema>;
export type SubscriptionHelperSubject = z.infer<typeof SubscriptionHelperSubjectSchema>;
export type SubscriptionHelperResponse = z.infer<typeof SubscriptionHelperResponseSchema>;
export type SubscriptionHelperQuery = z.infer<typeof SubscriptionHelperQuerySchema>;

export const SUBSCRIPTION_TRACE_STEP_LABELS: Record<SubscriptionTraceStepKey, string> = {
  PENDING_SIGNUP_CREATED: 'Inscription en attente créée',
  CHECKOUT_SESSION_CREATED: 'Session checkout Stripe créée',
  CHECKOUT_COMPLETED: 'Checkout Stripe complété',
  USER_CREATED: 'Compte utilisateur créé',
  GROUPE_SUBSCRIPTION_ACTIVATED: 'Groupe / abonnement activé',
  WELCOME_EMAIL_SENT: 'Email de bienvenue envoyé',
  VERIFICATION_EMAIL_SENT: 'Email de vérification envoyé',
  REGISTRATION_SMS_SENT: 'SMS d\'inscription envoyé',
  PAYMENT_CONFIRMATION_EMAIL_SENT: 'Email de confirmation paiement envoyé',
};
